import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FiBell, FiChevronDown, FiChevronUp, FiSearch } from "react-icons/fi";

import { getOverview, listONTs, OVERVIEW_REFRESH_MS } from "@/lib/api";
import { useSignalBands } from "@/lib/bands";
import { fmtNum, useI18n, type Key } from "@/lib/i18n";
import { rxLevel, type RxLevel } from "@/lib/olthealth";
import { causeKind, lastOutage, ontLive, triageRank, type CauseKind } from "@/lib/ontstate";
import { fmtDbm } from "@/lib/signal";
import { cn } from "@/lib/utils";
import type { ONT } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { agoText, causeLabel, distText, durText, LiveBadge, RX_UI, RxTag } from "@/components/OntStatus";

type Quick = "all" | "offline" | "weak" | "alerts" | "unlinked" | "stale";
type SortKey = "triage" | "status" | "rx" | "distance" | "seen" | "outage" | "olt";
const PAGE = 100;

function useNow() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

export default function ONTsPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const bands = useSignalBands();
  const now = useNow();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const olt = params.get("olt") ?? "all";
  const pon = params.get("pon") ?? "all";
  const quick = (params.get("f") ?? "all") as Quick;
  const optical = (params.get("rx") ?? "all") as RxLevel | "all";
  const reason = (params.get("cause") ?? "all") as CauseKind | "none" | "all";
  const sort = (params.get("sort") ?? "triage") as SortKey;
  const desc = params.get("dir") === "desc";
  const page = Math.max(0, Number(params.get("page") ?? 0) || 0);
  const [text, setText] = useState(q);

  const set = (next: Record<string, string | null>) =>
    setParams((p) => {
      for (const [k, v] of Object.entries(next)) (v === null || v === "all" ? p.delete(k) : p.set(k, v));
      if (!("page" in next)) p.delete("page");
      return p;
    }, { replace: true });

  // Debounce the search box into the URL: the server searches serial,
  // description, notes, learned MACs and the CPE's WAN IP / MAC.
  useEffect(() => {
    const id = setTimeout(() => { if (text !== q) set({ q: text || null }); }, 300);
    return () => clearTimeout(id);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data, error, isLoading } = useQuery({
    queryKey: ["onts", q],
    queryFn: () => listONTs({ q, limit: 5000 }),
    refetchInterval: 60_000,
  });
  const ov = useQuery({ queryKey: ["overview"], queryFn: getOverview, refetchInterval: OVERVIEW_REFRESH_MS });
  const pollS = ov.data?.olt_poll_interval_s ?? 120;
  const all = useMemo(() => data?.onts ?? [], [data]);

  // Quick-filter predicates double as chip counts.
  const is: Record<Quick, (o: ONT) => boolean> = {
    all: () => true,
    offline: (o) => { const l = ontLive(o, pollS, now); return l === "offline" || l === "missing"; },
    weak: (o) => o.run_state === "Online" && ["weak", "critical"].includes(rxLevel(o.rx_power_dbm, bands)),
    alerts: (o) => (o.open_alerts ?? 0) > 0,
    unlinked: (o) => o.subscriber_id === null || o.subscriber_id === undefined,
    stale: (o) => ontLive(o, pollS, now) === "stale",
  };
  const scoped = all.filter((o) => (olt === "all" || o.olt_name === olt) && (pon === "all" || o.pon_path === pon));
  const rows = scoped.filter((o) =>
    is[quick](o) &&
    (optical === "all" || (o.run_state === "Online" ? rxLevel(o.rx_power_dbm, bands) : "nodata") === optical) &&
    (reason === "all" || (causeKind(o.last_down_cause) ?? "none") === reason));

  const cmpName = (a: ONT, b: ONT) =>
    a.olt_name.localeCompare(b.olt_name) || a.pon_path.localeCompare(b.pon_path, undefined, { numeric: true }) || a.ont_id - b.ont_id;
  const outageMs = (o: ONT) => lastOutage(o, now)?.durationMs ?? -1;
  const key: Record<SortKey, (a: ONT, b: ONT) => number> = {
    triage: (a, b) => triageRank(a, bands, pollS, now) - triageRank(b, bands, pollS, now) || outageMs(b) - outageMs(a) || cmpName(a, b),
    status: (a, b) => triageRank(a, bands, pollS, now) - triageRank(b, bands, pollS, now) || cmpName(a, b),
    rx: (a, b) => (a.rx_power_dbm ?? 99) - (b.rx_power_dbm ?? 99) || cmpName(a, b),
    distance: (a, b) => (b.distance_m ?? -1) - (a.distance_m ?? -1) || cmpName(a, b),
    seen: (a, b) => (Date.parse(a.last_seen_at ?? "") || 0) - (Date.parse(b.last_seen_at ?? "") || 0) || cmpName(a, b),
    outage: (a, b) => outageMs(b) - outageMs(a) || cmpName(a, b),
    olt: cmpName,
  };
  const sorted = [...rows].sort((a, b) => (desc ? -1 : 1) * key[sort](a, b));
  const pages = Math.max(1, Math.ceil(sorted.length / PAGE));
  const shown = sorted.slice(page * PAGE, page * PAGE + PAGE);

  const olts = [...new Set(all.map((o) => o.olt_name))].sort();
  const pons = [...new Set(all.filter((o) => olt === "all" || o.olt_name === olt).map((o) => o.pon_path))]
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const chips: Quick[] = ["all", "offline", "weak", "alerts", "unlinked", "stale"];

  const Th = ({ k, label, right }: { k: SortKey; label: string; right?: boolean }) => (
    <th className={cn("px-3 py-2.5 font-medium", right && "text-right")}>
      <button type="button" onClick={() => set({ sort: k === "triage" ? null : k, dir: sort === k && !desc ? "desc" : null })}
        className={cn("eyebrow inline-flex items-center gap-1 hover:text-slate-200", sort === k && "text-slate-200")}>
        {label}{sort === k && (desc ? <FiChevronUp /> : <FiChevronDown />)}
      </button>
    </th>
  );
  const select = (value: string, onChange: (v: string) => void, options: [string, string][], label: string) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}
      className="h-8 rounded-sm border border-rule bg-panel px-2 text-[12px] text-slate-300">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-100">{t("nav.onts")}</h1>
          <p className="data mt-1 text-[12.5px] text-slate-400">
            {t("ont.list.summary", { n: fmtNum(locale, scoped.length), off: fmtNum(locale, scoped.filter(is.offline).length) })}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {chips.map((c) => {
          const n = scoped.filter(is[c]).length;
          return (
            <button key={c} type="button" onClick={() => set({ f: c })}
              className={cn("rounded-sm border px-2 py-1 text-[12px] transition-colors",
                quick === c ? "border-signal/50 bg-signal/10 text-slate-100" : "border-rule text-slate-400 hover:text-slate-100",
                c === "offline" && n > 0 && quick !== c && "text-signal-crit")}>
              {t(`ont.quick.${c}` as Key)} <span className="data text-slate-500">{fmtNum(locale, n)}</span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-80">
          <FiSearch className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-slate-500" />
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("ont.list.search")}
            className="h-8 border-rule bg-panel pl-8 text-[12.5px]" />
        </div>
        {select(olt, (v) => set({ olt: v, pon: null }), [["all", t("onts.allOlts")], ...olts.map((o) => [o, o] as [string, string])], "OLT")}
        {select(pon, (v) => set({ pon: v }), [["all", t("onts.allPons")], ...pons.map((p) => [p, `PON ${p}`] as [string, string])], "PON")}
        {select(optical, (v) => set({ rx: v }), [["all", t("ont.list.anyOptical")],
          ...(["good", "moderate", "weak", "critical", "nodata"] as RxLevel[]).map((l) => [l, t(RX_UI[l].label)] as [string, string])], t("ont.col.optical"))}
        {select(reason, (v) => set({ cause: v }), [["all", t("ont.list.anyCause")], ["dyingGasp", t("ont.cause.dyingGasp")],
          ["los", t("ont.cause.los")], ["other", t("ont.cause.other")], ["none", t("ont.list.neverDown")]], t("ont.col.outage"))}
      </div>

      {isLoading && <p className="text-slate-400">{t("common.loading")}</p>}
      {error && <p className="text-signal-crit">{(error as Error).message}</p>}

      {data && (
        <div className="overflow-x-auto rounded-lg border border-rule bg-panel">
          <table className="w-full min-w-[1100px] text-[13px]">
            <thead>
              <tr className="border-b border-rule text-left">
                <Th k="olt" label={t("ont.col.ont")} />
                <Th k="status" label={t("common.status")} />
                <Th k="olt" label={t("ont.col.oltPon")} />
                <Th k="rx" label={t("ont.col.optical")} right />
                <Th k="distance" label={t("onts.distance")} right />
                <Th k="outage" label={t("ont.col.outage")} />
                <Th k="seen" label={t("common.lastSeen")} right />
              </tr>
            </thead>
            <tbody>
              {shown.map((o) => {
                const live = ontLive(o, pollS, now);
                const up = o.run_state === "Online";
                // The latest poll came back "--" but an earlier one had a value: shown, greyed, with its age.
                const carried = up && !!o.rx_at && !!o.optical_at && Date.parse(o.rx_at) < Date.parse(o.optical_at);
                const lvl = up ? rxLevel(o.rx_power_dbm, bands) : "nodata";
                const out = lastOutage(o, now);
                return (
                  <tr key={o.id} onClick={() => navigate(`/onts/${o.id}`)} className="group cursor-pointer border-b border-rule/60 last:border-b-0 hover:bg-white/[0.02]">
                    <td className="max-w-0 px-3 py-2">
                      <Link to={`/onts/${o.id}`} onClick={(e) => e.stopPropagation()} className="block truncate font-medium text-slate-100 group-hover:text-signal">
                        {o.description || o.serial}
                      </Link>
                      <div className="data truncate text-[11.5px] text-slate-500">{o.serial} · #{o.ont_id}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <LiveBadge live={live} />
                        {(o.open_alerts ?? 0) > 0 && (
                          <span title={t("ont.list.alertsN", { n: o.open_alerts ?? 0 })}
                            className={cn("inline-flex items-center gap-0.5 text-[11px]", (o.critical_alerts ?? 0) > 0 ? "text-signal-crit" : "text-signal-warn")}>
                            <FiBell className="size-3" />{o.open_alerts}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <Link to={`/olts/${o.olt_id}`} onClick={(e) => e.stopPropagation()} className="block truncate text-slate-300 hover:text-signal">{o.olt_name}</Link>
                      <Link to={`/olts/${o.olt_id}?pon=${encodeURIComponent(o.pon_path)}`} onClick={(e) => e.stopPropagation()}
                        className="data text-[11.5px] text-slate-500 hover:text-signal">PON {o.pon_path}</Link>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {/* Offline: its last reading is history, drawn grey and labelled as such. */}
                      {/* Two lines like every other cell: reading + level word, then the upstream reading. */}
                      <div className="flex items-baseline justify-end gap-2">
                        {up && !carried && <RxTag level={lvl} />}
                        {carried && <span className="text-[10.5px] text-signal-warn" title={t("ont.opt.noReadingWhy")}>{t("ont.opt.noReading", { age: agoText(o.rx_at, now) })}</span>}
                        <span className={cn("data", up && !carried ? RX_UI[lvl].text : "text-slate-500")}>
                          {up ? fmtDbm(o.rx_power_dbm) : o.rx_power_dbm !== null ? `${t("ont.list.last")} ${fmtDbm(o.rx_power_dbm)}` : "—"}
                        </span>
                      </div>
                      {up && o.olt_rx_dbm !== null && <div className="data text-[11px] text-slate-500" title={t("ont.opt.oltRxDir")}>↑ {fmtDbm(o.olt_rx_dbm)}</div>}
                    </td>
                    <td className="data px-3 py-2 text-right text-slate-400">{distText(o.distance_m)}</td>
                    <td className="px-3 py-2">
                      {!out ? <span className="text-slate-600">—</span> : out.ongoing ? (
                        <>
                          <div className="text-[12.5px] font-medium text-signal-crit">{causeLabel(out.kind, t)}</div>
                          <div className="data text-[11.5px] text-signal-crit/80">{t("ont.list.downFor", { d: durText(out.durationMs) })}</div>
                        </>
                      ) : (
                        // A resolved outage is history: muted, dated, never a badge.
                        <>
                          <div className="text-[12px] text-slate-400">{causeLabel(out.kind, t)}</div>
                          <div className="data text-[11px] text-slate-500">{out.startedAt ? t("ont.list.ago", { d: agoText(out.startedAt, now) }) : "—"}</div>
                        </>
                      )}
                    </td>
                    <td className={cn("data px-3 py-2 text-right text-[12px]", live === "stale" ? "text-amber-200" : "text-slate-500")}>
                      {agoText(o.last_seen_at, now)}
                    </td>
                  </tr>
                );
              })}
              {shown.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">{t("onts.empty")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between text-[12px] text-slate-400">
          <span className="data">{t("ont.list.range", { a: page * PAGE + 1, b: Math.min(sorted.length, page * PAGE + PAGE), n: fmtNum(locale, sorted.length) })}</span>
          <span className="flex gap-1">
            <Button size="xs" variant="secondary" disabled={page === 0} onClick={() => set({ page: String(page - 1) })}>‹</Button>
            <span className="data px-2 py-0.5">{page + 1} / {pages}</span>
            <Button size="xs" variant="secondary" disabled={page >= pages - 1} onClick={() => set({ page: String(page + 1) })}>›</Button>
          </span>
        </div>
      )}
    </div>
  );
}
